import { useLazyQuery } from "@apollo/client";
import { useCallback, useEffect, useRef } from "react";
import { Form } from "react-router-dom";
import { Button, Card, CardBody, CardTitle } from "reactstrap";
import { TOTAL_POSITIONS_BY_POSITION_TITLE } from "../../../graphql/position-gql";
import { Action } from "../../../types/reducer-action";
import { toNumber } from "../../../utils/commons";
import AccordionContainer from "../../common/accordion-container";
import AutoFocusDiv from "../../common/auto-focus-div";
import BusyButton from "../../common/busy-button";
import ChangeAlert, { ChangeAlertRef } from "../../common/change-alert";
import confirm from "../../common/confirm";
import { DataEntryEvent } from "../../common/data-entry-reducer";
import { CheckIcon, RefreshIcon, SaveIcon } from "../../common/icons";
import PositionInfoPanel from "./position-info-panel";
import { usePositionTitleDispatch, usePositionTitleState } from "./position-manager-context";
import usePositionTitleApi, { isSamePositionTitle } from "./usePositionTitleApi";

export interface PositionDetailsPaneProps {
  showDuplicateConfirmation(): void
}

function PositionDetailsPane(props: PositionDetailsPaneProps) {
  const { dataSet, selectedDataId, dataDetails, form } = usePositionTitleState();
  const { showDuplicateConfirmation } = props;

  const dispatch = usePositionTitleDispatch();

  const { updatePositionTitle, busy } = usePositionTitleApi();

  const [countPositionsByTitle] = useLazyQuery<{ totalPositionsByPositionTitle: number }>(TOTAL_POSITIONS_BY_POSITION_TITLE);

  const changeAlertRef = useRef<ChangeAlertRef>(null);

  useEffect(() => {
    !!selectedDataId && dispatch(new Action(DataEntryEvent.SetDataDetails,
      dataSet?.find(item => item.positionTitleId === selectedDataId)
    ));
  }, [dataSet, selectedDataId]);

  const reset = () => dispatch(new Action(DataEntryEvent.ResetForm));

  const isDirty = useCallback(() => !isSamePositionTitle(dataDetails, form), [dataDetails, form]);

  const handleSubmit = useCallback(async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!selectedDataId) return;

    const formData = new FormData(event.currentTarget as HTMLFormElement);
    const input = { ...form, categoryId: toNumber(formData.get('categoryId'), 0) }

    let showUpdateTitleConfirm = form.positionName !== dataDetails?.positionName;

    if (!form.status && dataDetails?.status) {
      // disabling an active position/role title
      const countResponse = await countPositionsByTitle({ variables: { positionTitleId: dataDetails.positionTitleId } });

      try {
        await confirm({
          title: 'Warning',
          content: `There are currently ${countResponse?.data?.totalPositionsByPositionTitle ?? 0} positions/roles with this job title. The deactivated position/role title and any individuals associated with the position/role title will still be displayed under the Positions/Roles & Individuals section and will still be searchable.`,
          okText: <><CheckIcon /> OK </>,
          okBtnColor: 'primary',
          titleCenter: true,
          bodyCenter: true
        });

        showUpdateTitleConfirm = false;
      }
      catch(e) {
        // user clicked cancel
        return;
      }
    }

    if (showUpdateTitleConfirm) {
      try {
        await confirm({
          title: 'Update the Title?',
          content: 'The old position/role title will be replaced by the updated position/role title for all the entries and individuals associated with that old position/role title under the Positions/Roles & Individuals module.',
          okText: <><CheckIcon /> Confirm </>,
          okBtnColor: 'danger'
        });
      }
      catch(e) {
        // user clicked cancel
        return;
      }
    }

    try {
      const success = await updatePositionTitle(selectedDataId, input);
      changeAlertRef.current?.complete(success);
    }
    catch(e) {
      // duplicate position / role title
      showDuplicateConfirmation();
    }    
  }, [selectedDataId, form]);

  const handleReset = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    reset();
  }

  if (!dataDetails) return <></>;

  return (
    <Card>
      <CardBody>
        <CardTitle>
          <AutoFocusDiv trigger={dataDetails}>
            Position/Role Title Information: {`${dataDetails?.positionName}`}
          </AutoFocusDiv>
        </CardTitle>

        <ChangeAlert isOpen={isDirty()}
          itemName={`position/role "${form.positionName}"`}
          onDiscard={reset}
          ref={changeAlertRef} />

        <Form className="oms-container" onSubmit={handleSubmit} onReset={handleReset}>
          <div className="oms-row">
            <BusyButton type="submit" aria-label="Save" color="primary"
              disabled={!isDirty()}
              value="Save" busy={busy}>
              <SaveIcon />
            </BusyButton>

            <Button type="reset" aria-label="Reset" color="secondary"
              disabled={busy} >
              <RefreshIcon /> Reset
            </Button>
          </div>

          <AccordionContainer title="Position Information" titleClass="fs-6">
            <PositionInfoPanel wide />
          </AccordionContainer>

        </Form>
      </CardBody>

    </Card>
  )
};

export default PositionDetailsPane;
